"use client"

import React, { useState, useEffect, useCallback, useRef } from "react"
import { X, Phone, User, MapPin, Store, ShoppingBag, Truck, ChevronRight, ChevronLeft, Search, Plus, Minus, Check, AlertCircle, Navigation } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { customerAPI, deliveryOrdersAPI, restaurantAPI, foodItemsAPI } from "../services/api"

interface Customer {
    _id: string
    phoneNumber: string
    username: string
    fullName?: string
    savedAddresses: Array<{
        _id: string
        label: string
        fullAddress?: string
        street?: string
        city?: string
        coordinates?: { lat: number; lng: number }
        isDefault: boolean
    }>
}

interface Restaurant {
    _id: string
    name: string
    logo?: string
    address?: { street?: string; city?: string }
    deliveryFee?: number
    minimumOrder?: number
}

interface FoodItem {
    _id: string
    name: string
    nameAr?: string
    price: number
    image?: string
    category: string
    sizes?: Array<{ name: string; price: number }>
    isAvailable: boolean
}

interface CartItem {
    foodItem: FoodItem
    quantity: number
    size?: string
    price: number
}

interface Captain {
    _id: string
    name: string
    phone: string
    isOnline: boolean
    vehicleType?: string
}

interface PhoneOrderWizardProps {
    onClose: () => void
    onSuccess: () => void
}

const PhoneOrderWizard: React.FC<PhoneOrderWizardProps> = ({ onClose, onSuccess }) => {
    const { t, isRTL, language } = useLanguage()
    const { toast } = useToast()

    const [step, setStep] = useState(1)
    const [loading, setLoading] = useState(false)

    // Step 1: Customer
    const [phoneNumber, setPhoneNumber] = useState("")
    const [searchingCustomer, setSearchingCustomer] = useState(false)
    const [customer, setCustomer] = useState<Customer | null>(null)
    const [isNewCustomer, setIsNewCustomer] = useState(false)
    const [newCustomerName, setNewCustomerName] = useState("")

    // Step 2: Address
    const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null)
    const [newAddress, setNewAddress] = useState({
        label: "Home",
        street: "",
        city: "",
        fullAddress: "",
        notes: "",
    })
    const [addressCoordinates, setAddressCoordinates] = useState<{ lat: number; lng: number } | null>(null)
    const [useNewAddress, setUseNewAddress] = useState(false)
    const [mapLoaded, setMapLoaded] = useState(false)
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapInstanceRef = useRef<any>(null)
    const markerRef = useRef<any>(null)

    // Step 3: Restaurant
    const [restaurants, setRestaurants] = useState<Restaurant[]>([])
    const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null)
    const [restaurantSearch, setRestaurantSearch] = useState("")

    // Step 4: Food Items
    const [foodItems, setFoodItems] = useState<FoodItem[]>([])
    const [loadingFoodItems, setLoadingFoodItems] = useState(false)
    const [cart, setCart] = useState<CartItem[]>([])
    const [selectedCategory, setSelectedCategory] = useState<string>("all")
    const [categories, setCategories] = useState<string[]>([])

    // Step 5: Captain & Confirm
    const [captains, setCaptains] = useState<Captain[]>([])
    const [selectedCaptainId, setSelectedCaptainId] = useState<string | null>(null)
    const [specialInstructions, setSpecialInstructions] = useState("")
    const [submitting, setSubmitting] = useState(false)

    // Search customer by phone
    const handleSearchCustomer = async () => {
        if (!phoneNumber.trim() || phoneNumber.length < 8) {
            toast({ title: t("error"), description: t("enterValidPhone"), variant: "destructive" })
            return
        }

        try {
            setSearchingCustomer(true)
            const response = await customerAPI.searchByPhone(phoneNumber)
            if (response.data.success && response.data.data.found) {
                setCustomer(response.data.data.customer)
                setIsNewCustomer(false)
                if (response.data.data.customer.savedAddresses?.length > 0) {
                    const defaultAddr = response.data.data.customer.savedAddresses.find((a: any) => a.isDefault)
                    setSelectedAddressId(defaultAddr?._id || response.data.data.customer.savedAddresses[0]._id)
                }
            } else {
                setCustomer(null)
                setIsNewCustomer(true)
            }
        } catch (error) {
            console.error("Search customer error:", error)
            setIsNewCustomer(true)
        } finally {
            setSearchingCustomer(false)
        }
    }

    // Fetch restaurants
    const fetchRestaurants = useCallback(async () => {
        try {
            const response = await restaurantAPI.getRestaurants({ limit: 100, isActive: true })
            setRestaurants(response.data.restaurants || [])
        } catch (error) {
            console.error("Failed to fetch restaurants:", error)
        }
    }, [])

    // Fetch food items for selected restaurant
    const fetchFoodItems = useCallback(async () => {
        if (!selectedRestaurant) return
        setLoadingFoodItems(true)
        try {
            const response = await foodItemsAPI.getFoodItemsByRestaurant(selectedRestaurant._id, { isAvailable: true })
            console.log("Food items response:", response.data)
            // API returns { success: true, data: [...foodItems] }
            const items = Array.isArray(response.data.data)
                ? response.data.data
                : (response.data.foodItems || response.data.data?.foodItems || [])
            console.log("Parsed food items:", items.length)
            setFoodItems(items)
            const cats = Array.from(new Set(items.map((item: FoodItem) => item.category))) as string[]
            setCategories(cats)
        } catch (error) {
            console.error("Failed to fetch food items:", error)
        } finally {
            setLoadingFoodItems(false)
        }
    }, [selectedRestaurant])

    // Fetch available captains
    const fetchCaptains = useCallback(async () => {
        try {
            const response = await deliveryOrdersAPI.getAvailableCaptains()
            setCaptains(response.data.data || response.data.captains || [])
        } catch (error) {
            console.error("Failed to fetch captains:", error)
        }
    }, [])

    useEffect(() => {
        if (step === 3) fetchRestaurants()
        if (step === 4 && selectedRestaurant) fetchFoodItems()
        if (step === 5) fetchCaptains()
    }, [step, selectedRestaurant, fetchRestaurants, fetchFoodItems, fetchCaptains])

    // Initialize map for address selection
    useEffect(() => {
        if (step !== 2 || !useNewAddress) return

        const w = window as any

        // Check if Google Maps is loaded
        if (!w.google?.maps?.Map) {
            // Try to load it
            const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY
            if (!apiKey) return

            const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')
            if (existingScript) {
                const checkLoaded = setInterval(() => {
                    if (w.google?.maps?.Map) {
                        setMapLoaded(true)
                        clearInterval(checkLoaded)
                    }
                }, 100)
                setTimeout(() => clearInterval(checkLoaded), 10000)
            }
            return
        }

        setMapLoaded(true)
    }, [step, useNewAddress])

    // Create map when loaded
    useEffect(() => {
        if (!mapLoaded || !mapContainerRef.current || mapInstanceRef.current) return
        if (step !== 2 || !useNewAddress) return

        const w = window as any
        if (!w.google?.maps?.Map) return

        try {
            // Default to Khartoum, Sudan
            const defaultCenter = addressCoordinates || { lat: 15.5007, lng: 32.5599 }

            mapInstanceRef.current = new w.google.maps.Map(mapContainerRef.current, {
                center: defaultCenter,
                zoom: 14,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
            })

            // Add draggable marker
            markerRef.current = new w.google.maps.Marker({
                position: defaultCenter,
                map: mapInstanceRef.current,
                draggable: true,
                icon: {
                    path: w.google.maps.SymbolPath.CIRCLE,
                    scale: 12,
                    fillColor: "#EF4444",
                    fillOpacity: 1,
                    strokeColor: "#FFFFFF",
                    strokeWeight: 3,
                },
            })

            // Update coordinates when marker is dragged
            markerRef.current.addListener("dragend", () => {
                const pos = markerRef.current.getPosition()
                const newCoords = { lat: pos.lat(), lng: pos.lng() }
                setAddressCoordinates(newCoords)

                // Reverse geocode to get address
                const geocoder = new w.google.maps.Geocoder()
                geocoder.geocode({ location: newCoords }, (results: any, status: any) => {
                    if (status === "OK" && results[0]) {
                        setNewAddress(prev => ({
                            ...prev,
                            fullAddress: results[0].formatted_address
                        }))
                    }
                })
            })

            // Also allow clicking on map to move marker
            mapInstanceRef.current.addListener("click", (e: any) => {
                const newCoords = { lat: e.latLng.lat(), lng: e.latLng.lng() }
                markerRef.current.setPosition(newCoords)
                setAddressCoordinates(newCoords)

                // Reverse geocode
                const geocoder = new w.google.maps.Geocoder()
                geocoder.geocode({ location: newCoords }, (results: any, status: any) => {
                    if (status === "OK" && results[0]) {
                        setNewAddress(prev => ({
                            ...prev,
                            fullAddress: results[0].formatted_address
                        }))
                    }
                })
            })

            // Set initial coordinates
            if (!addressCoordinates) {
                setAddressCoordinates(defaultCenter)
            }
        } catch (err) {
            console.error("Failed to initialize address map:", err)
        }

        return () => {
            if (markerRef.current) {
                markerRef.current.setMap(null)
                markerRef.current = null
            }
            mapInstanceRef.current = null
        }
    }, [mapLoaded, step, useNewAddress])

    // Reset map when switching away from new address
    useEffect(() => {
        if (!useNewAddress) {
            if (markerRef.current) {
                markerRef.current.setMap(null)
                markerRef.current = null
            }
            mapInstanceRef.current = null
        }
    }, [useNewAddress])

    const addToCart = (item: FoodItem, size?: string) => {
        const price = size ? (item.sizes?.find(s => s.name === size)?.price || item.price) : item.price
        const existingIndex = cart.findIndex(c => c.foodItem._id === item._id && c.size === size)

        if (existingIndex >= 0) {
            const newCart = [...cart]
            newCart[existingIndex].quantity += 1
            setCart(newCart)
        } else {
            setCart([...cart, { foodItem: item, quantity: 1, size, price }])
        }
    }

    const updateCartQuantity = (index: number, delta: number) => {
        const newCart = [...cart]
        newCart[index].quantity += delta
        if (newCart[index].quantity <= 0) {
            newCart.splice(index, 1)
        }
        setCart(newCart)
    }

    const getCartTotal = () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const getDeliveryFee = () => selectedRestaurant?.deliveryFee || 0
    const getGrandTotal = () => getCartTotal() + getDeliveryFee()

    const formatCurrency = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SDG" }).format(amount)

    const handleSubmitOrder = async () => {
        try {
            setSubmitting(true)

            // Prepare delivery address
            let deliveryAddress
            if (useNewAddress || !selectedAddressId) {
                deliveryAddress = {
                    street: newAddress.street,
                    city: newAddress.city,
                    fullAddress: newAddress.fullAddress || `${newAddress.street}, ${newAddress.city}`,
                    notes: newAddress.notes,
                    coordinates: addressCoordinates,
                }
            } else {
                const addr = customer?.savedAddresses.find(a => a._id === selectedAddressId)
                deliveryAddress = {
                    street: addr?.street,
                    city: addr?.city,
                    fullAddress: addr?.fullAddress || `${addr?.street}, ${addr?.city}`,
                    coordinates: addr?.coordinates,
                }
            }

            // Prepare order data
            const orderData = {
                customerId: customer?._id,
                customerPhone: phoneNumber,
                customerName: customer?.fullName || customer?.username || newCustomerName,
                isNewCustomer,
                newCustomerData: isNewCustomer ? { phoneNumber, username: newCustomerName || phoneNumber, fullName: newCustomerName } : undefined,
                restaurantId: selectedRestaurant?._id,
                items: cart.map(item => ({
                    foodItemId: item.foodItem._id,
                    name: item.foodItem.name,
                    quantity: item.quantity,
                    price: item.price,
                    size: item.size ? { name: item.size, price: item.price } : undefined,
                })),
                deliveryAddress,
                captainId: selectedCaptainId || undefined,
                specialInstructions,
                pricing: {
                    subtotal: getCartTotal(),
                    deliveryFee: getDeliveryFee(),
                    total: getGrandTotal(),
                },
                saveNewAddress: useNewAddress && newAddress.fullAddress ? {
                    label: newAddress.label,
                    fullAddress: newAddress.fullAddress,
                    coordinates: addressCoordinates,
                    notes: newAddress.notes,
                } : undefined,
            }

            const response = await deliveryOrdersAPI.createPhoneOrder(orderData)

            if (response.data.success) {
                toast({ title: t("success"), description: t("orderCreatedSuccessfully") })
                onSuccess()
                onClose()
            } else {
                throw new Error(response.data.message || "Failed to create order")
            }
        } catch (error: any) {
            console.error("Submit order error:", error)
            toast({
                title: t("error"),
                description: error.response?.data?.message || t("failedToCreateOrder"),
                variant: "destructive",
            })
        } finally {
            setSubmitting(false)
        }
    }

    const canProceed = () => {
        switch (step) {
            case 1: return phoneNumber.length >= 8 && (customer || (isNewCustomer && newCustomerName))
            case 2:
                if (selectedAddressId && !useNewAddress) {
                    // Using saved address - check if it has coordinates
                    const addr = customer?.savedAddresses.find(a => a._id === selectedAddressId)
                    return addr?.coordinates?.lat && addr?.coordinates?.lng
                }
                // Using new address - require coordinates from map
                return addressCoordinates?.lat && addressCoordinates?.lng
            case 3: return selectedRestaurant !== null
            case 4: return cart.length > 0
            case 5: return true
            default: return false
        }
    }

    const filteredRestaurants = restaurants.filter(r =>
        r.name.toLowerCase().includes(restaurantSearch.toLowerCase())
    )

    const filteredFoodItems = foodItems.filter(item =>
        selectedCategory === "all" || item.category === selectedCategory
    )

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t("newPhoneOrder")}</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t("step")} {step} {t("of")} 5</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Progress Bar */}
                <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((s) => (
                            <React.Fragment key={s}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${s < step ? "bg-green-500 text-white" : s === step ? "bg-red-600 text-white" : "bg-gray-200 dark:bg-gray-600 text-gray-500"
                                    }`}>
                                    {s < step ? <Check className="w-4 h-4" /> : s}
                                </div>
                                {s < 5 && <div className={`flex-1 h-1 ${s < step ? "bg-green-500" : "bg-gray-200 dark:bg-gray-600"}`} />}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-400">
                        <span>{t("customer")}</span>
                        <span>{t("address")}</span>
                        <span>{t("restaurant")}</span>
                        <span>{t("items")}</span>
                        <span>{t("confirm")}</span>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {/* Step 1: Customer */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t("customerPhone")}</label>
                                <div className="flex gap-2">
                                    <div className="relative flex-1">
                                        <Phone className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? "right-3" : "left-3"}`} />
                                        <input
                                            type="tel"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            onKeyDown={(e) => e.key === "Enter" && handleSearchCustomer()}
                                            placeholder={t("enterPhoneNumber")}
                                            className={`w-full ${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                                        />
                                    </div>
                                    <button
                                        onClick={handleSearchCustomer}
                                        disabled={searchingCustomer}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                    >
                                        {searchingCustomer ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            {customer && (
                                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                                            <User className="w-6 h-6 text-green-600 dark:text-green-400" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">{customer.fullName || customer.username}</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{customer.phoneNumber}</p>
                                            <p className="text-xs text-green-600 dark:text-green-400">{t("existingCustomer")} • {customer.savedAddresses.length} {t("savedAddresses")}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {isNewCustomer && (
                                <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg">
                                    <div className="flex items-center gap-2 mb-3">
                                        <AlertCircle className="w-5 h-5 text-orange-600" />
                                        <p className="font-medium text-orange-800 dark:text-orange-200">{t("newCustomer")}</p>
                                    </div>
                                    <input
                                        type="text"
                                        value={newCustomerName}
                                        onChange={(e) => setNewCustomerName(e.target.value)}
                                        placeholder={t("enterCustomerName")}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Address */}
                    {step === 2 && (
                        <div className="space-y-4">
                            {customer && customer.savedAddresses.length > 0 && !useNewAddress && (
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t("selectAddress")}</label>
                                    {customer.savedAddresses.map((addr) => (
                                        <div
                                            key={addr._id}
                                            onClick={() => setSelectedAddressId(addr._id)}
                                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${selectedAddressId === addr._id
                                                ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                                                : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <MapPin className={`w-5 h-5 ${selectedAddressId === addr._id ? "text-red-600" : "text-gray-400"}`} />
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-900 dark:text-white">{addr.label}</p>
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">{addr.fullAddress || `${addr.street}, ${addr.city}`}</p>
                                                    {!addr.coordinates && (
                                                        <p className="text-xs text-orange-500 mt-1">{t("noCoordinates")}</p>
                                                    )}
                                                </div>
                                                {addr.isDefault && <span className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">{t("default")}</span>}
                                                {addr.coordinates && <Navigation className="w-4 h-4 text-green-500" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <button
                                onClick={() => {
                                    setUseNewAddress(!useNewAddress)
                                    if (!useNewAddress) {
                                        // Reset map refs when switching to new address
                                        mapInstanceRef.current = null
                                        markerRef.current = null
                                    }
                                }}
                                className="flex items-center gap-2 text-red-600 hover:text-red-700"
                            >
                                <Plus className="w-4 h-4" />
                                {useNewAddress ? t("useSavedAddress") : t("addNewAddress")}
                            </button>

                            {(useNewAddress || !customer || customer.savedAddresses.length === 0) && (
                                <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    {/* Map for pin drop */}
                                    <div className="mb-4">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            <MapPin className="w-4 h-4 inline mr-1" />
                                            {t("dropPinOnMap")}
                                        </label>
                                        <div
                                            ref={mapContainerRef}
                                            className="w-full h-[250px] rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-200 dark:bg-gray-700"
                                        >
                                            {!mapLoaded && (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <div className="text-center">
                                                        <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                                        <p className="text-sm text-gray-500">{t("loadingMap")}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {addressCoordinates && (
                                            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                                                <Check className="w-3 h-3" />
                                                {t("locationSelected")}: {addressCoordinates.lat.toFixed(6)}, {addressCoordinates.lng.toFixed(6)}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-500 mt-1">{t("clickOrDragMarker")}</p>
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        {["Home", "Work", "Other"].map((label) => (
                                            <button
                                                key={label}
                                                onClick={() => setNewAddress({ ...newAddress, label })}
                                                className={`px-3 py-2 rounded-lg text-sm ${newAddress.label === label
                                                    ? "bg-red-600 text-white"
                                                    : "bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500"
                                                    }`}
                                            >
                                                {t(label.toLowerCase())}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        type="text"
                                        value={newAddress.fullAddress}
                                        onChange={(e) => setNewAddress({ ...newAddress, fullAddress: e.target.value })}
                                        placeholder={t("addressFromMap")}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                        readOnly
                                    />
                                    <textarea
                                        value={newAddress.notes}
                                        onChange={(e) => setNewAddress({ ...newAddress, notes: e.target.value })}
                                        placeholder={t("deliveryNotes")}
                                        rows={2}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Restaurant */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="relative">
                                <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? "right-3" : "left-3"}`} />
                                <input
                                    type="text"
                                    value={restaurantSearch}
                                    onChange={(e) => setRestaurantSearch(e.target.value)}
                                    placeholder={t("searchRestaurants")}
                                    className={`w-full ${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700`}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                                {filteredRestaurants.map((restaurant) => (
                                    <div
                                        key={restaurant._id}
                                        onClick={() => setSelectedRestaurant(restaurant)}
                                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${selectedRestaurant?._id === restaurant._id
                                            ? "border-red-500 bg-red-50 dark:bg-red-900/20"
                                            : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                                {restaurant.logo ? (
                                                    <img src={restaurant.logo} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <Store className="w-6 h-6 m-3 text-gray-400" />
                                                )}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-white">{restaurant.name}</p>
                                                <p className="text-xs text-gray-500">{t("deliveryFee")}: {formatCurrency(restaurant.deliveryFee || 0)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 4: Food Items */}
                    {step === 4 && (
                        <div className="flex gap-4 h-[500px]">
                            {/* Menu */}
                            <div className="flex-1 flex flex-col">
                                <div className="flex gap-2 mb-3 overflow-x-auto pb-2">
                                    <button
                                        onClick={() => setSelectedCategory("all")}
                                        className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${selectedCategory === "all" ? "bg-red-600 text-white" : "bg-gray-100 dark:bg-gray-700"
                                            }`}
                                    >
                                        {t("all")}
                                    </button>
                                    {categories.map((cat) => (
                                        <button
                                            key={cat}
                                            onClick={() => setSelectedCategory(cat)}
                                            className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${selectedCategory === cat ? "bg-red-600 text-white" : "bg-gray-100 dark:bg-gray-700"
                                                }`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                                <div className="flex-1 overflow-y-auto space-y-2">
                                    {loadingFoodItems ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                                            <span className="ml-2 text-gray-500">{t("loading")}</span>
                                        </div>
                                    ) : filteredFoodItems.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            {t("noFoodItemsFound")}
                                        </div>
                                    ) : (
                                        filteredFoodItems.map((item) => (
                                            <div key={item._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-600 overflow-hidden">
                                                        {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900 dark:text-white">{language === "ar" && item.nameAr ? item.nameAr : item.name}</p>
                                                        <p className="text-sm text-gray-500">{formatCurrency(item.price)}</p>
                                                    </div>
                                                </div>
                                                {item.sizes && item.sizes.length > 0 ? (
                                                    <div className="flex gap-1">
                                                        {item.sizes.map((size) => (
                                                            <button
                                                                key={size.name}
                                                                onClick={() => addToCart(item, size.name)}
                                                                className="px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200"
                                                            >
                                                                {size.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => addToCart(item)}
                                                        className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                                                    >
                                                        <Plus className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Cart */}
                            <div className="w-64 flex flex-col bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                <h3 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <ShoppingBag className="w-4 h-4" />
                                    {t("cart")} ({cart.length})
                                </h3>
                                <div className="flex-1 overflow-y-auto space-y-2">
                                    {cart.map((item, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium truncate">{item.foodItem.name}</p>
                                                {item.size && <p className="text-xs text-gray-500">{item.size}</p>}
                                                <p className="text-xs text-gray-500">{formatCurrency(item.price)}</p>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => updateCartQuantity(idx, -1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="w-6 text-center text-sm">{item.quantity}</span>
                                                <button onClick={() => updateCartQuantity(idx, 1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="border-t border-gray-200 dark:border-gray-600 pt-3 mt-3 space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span>{t("subtotal")}</span>
                                        <span>{formatCurrency(getCartTotal())}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>{t("deliveryFee")}</span>
                                        <span>{formatCurrency(getDeliveryFee())}</span>
                                    </div>
                                    <div className="flex justify-between font-bold">
                                        <span>{t("total")}</span>
                                        <span>{formatCurrency(getGrandTotal())}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Captain & Confirm */}
                    {step === 5 && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t("assignDriver")} ({t("optional")})</label>
                                <div className="grid grid-cols-2 gap-3 max-h-[200px] overflow-y-auto">
                                    <div
                                        onClick={() => setSelectedCaptainId(null)}
                                        className={`p-3 rounded-lg border cursor-pointer ${!selectedCaptainId ? "border-red-500 bg-red-50 dark:bg-red-900/20" : "border-gray-200 dark:border-gray-700"}`}
                                    >
                                        <p className="font-medium">{t("autoAssign")}</p>
                                        <p className="text-xs text-gray-500">{t("systemWillAssign")}</p>
                                    </div>
                                    {captains.map((captain) => (
                                        <div
                                            key={captain._id}
                                            onClick={() => setSelectedCaptainId(captain._id)}
                                            className={`p-3 rounded-lg border cursor-pointer ${selectedCaptainId === captain._id ? "border-red-500 bg-red-50 dark:bg-red-900/20" : "border-gray-200 dark:border-gray-700"}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Truck className="w-4 h-4" />
                                                <div>
                                                    <p className="font-medium">{captain.name}</p>
                                                    <p className="text-xs text-gray-500">{captain.phone}</p>
                                                </div>
                                                {captain.isOnline && <span className="ml-auto w-2 h-2 bg-green-500 rounded-full" />}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t("specialInstructions")}</label>
                                <textarea
                                    value={specialInstructions}
                                    onChange={(e) => setSpecialInstructions(e.target.value)}
                                    placeholder={t("anySpecialInstructions")}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                />
                            </div>

                            {/* Order Summary */}
                            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                <h3 className="font-medium mb-3">{t("orderSummary")}</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between"><span>{t("customer")}:</span><span>{customer?.fullName || newCustomerName}</span></div>
                                    <div className="flex justify-between"><span>{t("phone")}:</span><span>{phoneNumber}</span></div>
                                    <div className="flex justify-between"><span>{t("restaurant")}:</span><span>{selectedRestaurant?.name}</span></div>
                                    <div className="flex justify-between"><span>{t("items")}:</span><span>{cart.length} {t("items")}</span></div>
                                    <div className="flex justify-between font-bold pt-2 border-t"><span>{t("total")}:</span><span>{formatCurrency(getGrandTotal())}</span></div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setStep(s => s - 1)}
                        disabled={step === 1}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                    >
                        {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                        {t("back")}
                    </button>
                    {step < 5 ? (
                        <button
                            onClick={() => setStep(s => s + 1)}
                            disabled={!canProceed()}
                            className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                            {t("next")}
                            {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmitOrder}
                            disabled={submitting}
                            className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                            {submitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                            {t("placeOrder")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PhoneOrderWizard
